import { CameraOutlined, DeleteOutlined } from '@ant-design/icons';
import { Button, Popconfirm, Tooltip, message } from 'antd';
import PropTypes from 'prop-types';
import React, { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { uploadFileFromObject } from '../../api/common/image-upload.js';
import { removeErpProductPhoto, updateErpProductPhoto } from '../../api/product';
import styles from './ErpProductImageUpload.module.scss';

// class for the empty image placeholder: hover overlay "Click to upload image"
export const uploadPlaceholderClassName = styles.uploadPlaceholder

const MAX_SIZE_MB = 5
const ACCEPT = 'image/png,image/jpeg,image/webp,image/gif'
const MESSAGE_KEY = 'erp-product-image'

// Upload / replace / remove the image of an ERP-created product. Renders
// nothing for CMS products — their images come from the CMS.
// Without an image only the (hidden) file input is rendered; the page opens it
// via `ref.current.openFileDialog()` when the placeholder is clicked.
const ErpProductImageUpload = forwardRef(function ErpProductImageUpload({ product, hasImage, onUploaded, onRemoved, style }, ref) {
    const inputRef = useRef(null)
    const [uploading, setUploading] = useState(false)
    const [removing, setRemoving] = useState(false)
    const busy = uploading || removing

    useImperativeHandle(ref, () => ({
        openFileDialog: () => {
            if (!busy) {
                inputRef.current?.click()
            }
        },
    }), [busy])

    if (!product?.is_erp_product) {
        return null
    }

    const handleFile = async (file) => {
        if (!file.type?.startsWith('image/')) {
            message.error('Please choose an image file')
            return
        }
        if (file.size > MAX_SIZE_MB * 1024 * 1024) {
            message.error(`Image must be smaller than ${MAX_SIZE_MB} MB`)
            return
        }
        setUploading(true)
        message.loading({ content: 'Uploading image…', key: MESSAGE_KEY, duration: 0 })
        try {
            const imageId = await uploadFileFromObject(file)
            const { main_photos } = await updateErpProductPhoto(product.id, imageId)
            message.success({ content: 'Product image updated', key: MESSAGE_KEY })
            onUploaded(main_photos?.[0])
        }
        catch (e) {
            message.error({ content: e.message, key: MESSAGE_KEY })
        }
        finally {
            setUploading(false)
        }
    }

    const handleRemove = async () => {
        setRemoving(true)
        try {
            await removeErpProductPhoto(product.id)
            message.success('Product image removed')
            onRemoved()
        }
        catch (e) {
            message.error(e.message)
        }
        finally {
            setRemoving(false)
        }
    }

    return (
        <>
            <input
                ref={inputRef}
                type="file"
                accept={ACCEPT}
                hidden
                onChange={(e) => {
                    const file = e.target.files?.[0]
                    // reset so picking the same file again still fires onChange
                    e.target.value = ''
                    if (file) {
                        handleFile(file)
                    }
                }}
            />
            {hasImage && (
                <span style={{ display: 'inline-flex', gap: 6, ...style }}>
                    <Button size="small" icon={<CameraOutlined />} loading={uploading} disabled={removing} onClick={() => inputRef.current?.click()}>
                        Change image
                    </Button>
                    <Popconfirm title="Remove product image?" okText="Remove" okButtonProps={{ danger: true }} onConfirm={handleRemove} disabled={busy}>
                        <Tooltip title="Remove image">
                            <Button size="small" danger icon={<DeleteOutlined />} loading={removing} disabled={uploading} aria-label="Remove image" />
                        </Tooltip>
                    </Popconfirm>
                </span>
            )}
        </>
    )
})

ErpProductImageUpload.propTypes = {
    product: PropTypes.shape({
        id: PropTypes.number,
        is_erp_product: PropTypes.bool,
    }),
    hasImage: PropTypes.bool,
    onUploaded: PropTypes.func.isRequired,
    onRemoved: PropTypes.func.isRequired,
    style: PropTypes.object,
}

export default ErpProductImageUpload;
